import { type TransformDatasetInput, TransformDatasetInputSchema } from './schema.ts';
import { ProgressTransformDatasetTool, RenderTransformDatasetTool } from './TransformDatasetTool.render.tsx';
import type { TransformDatasetOutput } from './types.ts';
import { formatTransformDatasetMessage } from '#plugin-chat/components/tools/helpers/format-tool-message.ts';
import { QueryKnowledgeGraphTool } from '#plugin-chat/components/tools/query-knowledge-graph/QueryKnowledgeGraphTool.tsx';
import { ShowDashboardTool } from '#plugin-chat/components/tools/show-dashboard/ShowDashboardTool.tsx';
import { computeAutoStats } from '#plugin-chat/lib/data-pipeline/engine/auto-stats.ts';
import { transformStep } from '#plugin-chat/lib/data-pipeline/steps/transform.ts';
import type { Dataset } from '#plugin-chat/lib/data-pipeline/types.ts';
import { Json, ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';
import type { ProducerToolConfig } from '#plugin-chat/lib/tool/create-tool.types.ts';

/**
 * Tool for transforming an existing stored dataset through an ordered
 * pipeline of actions: filter, derive, sort, slice, rollup,
 * pivot, fold, spread, unroll.
 *
 * Resolves the input via `ctx.store` (`store.get(inputDataset)` returns a
 * fully typed `Dataset` — no rebuild) and delegates the transform to
 * {@link transformStep}. The engine's sibling outputs (warnings, global
 * aggregations, grouped aggregations) ride along on `dataset.meta` so the
 * tool can surface them when formatting the LLM message.
 *
 * Producer tool: the framework calls `produceDataset` first, stages the
 * result under `args.outputDataset`, and passes it into `execute`. Recovery
 * post-reload re-runs `produceDataset`, cascading into upstream recovery
 * for the `inputDataset` via `ctx.store.get(...)`.
 */
export const TransformDatasetTool = createTool<object, TransformDatasetInput, TransformDatasetOutput>(
  'transformDataset',
  () => ({
    instructions: (
      <ToolGuidance
        tool={TransformDatasetTool}
        whenNotToUse={[`For instance data (use ${QueryKnowledgeGraphTool.mention})`]}
        whenToUse={[
          'Fetching and aggregating timeseries datapoints from knowledge graph instances.',
          'Filtering, deriving, sorting, slicing, rolling up, or reshaping a stored dataset.',
          'Discovering the value domain of a categorical attribute — before filtering on an unfamiliar categorical column, run a rollup action on it with a count aggregation to reveal the actual distinct values and their frequencies. Prefer this over guessing a filter value that may return 0 matches with no indication the guess was wrong.',
          'Answering any "how many X" / sum / average / min/max / distribution question — must go through a rollup action, never inferred from a sample. Always prefer grouping without a pre-applied filter.',
          'Preparing a dataset for downstream tools (e.g. chaining into getDatapoints or showDashboard).',
        ]}
      >
        <h3>Pipeline of actions</h3>

        <p>
          Provide an ordered <code>actions</code> array. Each action receives the previous action's output as input.
          Order is significant — the same actions in a different order produce different results.
        </p>

        {/* <ul> */}
        {/*   <li> */}
        {/*     <code>filter</code> — keep rows that satisfy a predicate. Field: <code>where</code> (nested AND/OR/NOT */}
        {/*     condition tree). */}
        {/*   </li> */}
        {/*   <li> */}
        {/*     <code>derive</code> — add computed columns via math.js expressions. Field: <code>derives</code> (array of{' '} */}
        {/*     <code>{'{ name, expression }'}</code>). */}
        {/*   </li> */}
        {/*   <li> */}
        {/*     <code>sort</code> — order rows. Field: <code>keys</code> (array of <code>{'{ column, direction? }'}</code>). */}
        {/*   </li> */}
        {/*   <li> */}
        {/*     <code>slice</code> — pagination. Fields: <code>limit?</code>, <code>offset?</code>. */}
        {/*   </li> */}
        {/*   <li> */}
        {/*     <code>aggregate</code> — compute aggregations, optionally grouped. Fields: <code>columns?</code> (group */}
        {/*     keys; omit for global rollup), <code>aggregations</code> (array of <code>{'{ name, op, column? }'}</code>; */}
        {/*     ops: count, sum, min, max, avg, first, last). */}
        {/*   </li> */}
        {/*   <li> */}
        {/*     <code>pivot</code> — long → wide. Fields: <code>keyColumn</code>, <code>valueColumn</code>,{' '} */}
        {/*     <code>aggregation?</code>. */}
        {/*   </li> */}
        {/*   <li> */}
        {/*     <code>fold</code> — wide → long. Fields: <code>columns</code>, <code>keyName?</code>,{' '} */}
        {/*     <code>valueName?</code>. */}
        {/*   </li> */}
        {/*   <li> */}
        {/*     <code>spread</code> — array column → multiple columns. Fields: <code>column</code>, <code>limit?</code>.{' '} */}
        {/*     <strong>Not for timeseries datapoint arrays</strong> (output of <code>daily()</code>/<code>hourly()</code>/ */}
        {/*     <code>monthly()</code>/<code>datapoints()</code>) — see "Charting timeseries data" below. */}
        {/*   </li> */}
        {/*   <li> */}
        {/*     <code>unroll</code> — array column → multiple rows. Fields: <code>column</code>, <code>indexName?</code>,{' '} */}
        {/*     <code>limit?</code>. <strong>Not for timeseries datapoint arrays</strong> — see "Charting timeseries data" */}
        {/*     below. */}
        {/*   </li> */}
        {/* </ul> */}

        <h4>Derive expressions</h4>
        <dl>
          <dt>Arithmetic</dt>
          <dd>+, -, *, /, ^, %</dd>
          <dt>Comparisons</dt>
          <dd>==, !=, &lt;, &gt;, &lt;=, &gt;=</dd>
          <dt>String functions</dt>
          <dd>strlen(col), substr(col, start, len), strcat(a, b, ...), prop(obj, "key")</dd>
          <dt>Timeseries fetchers</dt>
          <dd>
            Require a timeseries-ref column (e.g. one returned by queryKnowledgeGraph): datapoints(ref, opts?),
            hourly(ref, opts?), daily(ref, opts?), weekly(ref, opts?), monthly(ref, opts?). Each returns an array of
            datapoints — {'{timestamp, value}'} for raw, {'{timestamp, <aggregate>}'} for the period fetchers (default
            aggregate "average"). opts: <code>{'{ start?, end?, aggregate?, limit? }'}</code>; default window is the
            last 90 days.
          </dd>
          <dt>Timeseries helpers</dt>
          <dd>
            first(arr), last(arr) pick an end element; value(dp) extracts a datapoint's numeric value; values(arr),
            timestamps(arr) extract aligned numeric / timestamp arrays. Use math.js built-in reducers (mean, min, max,
            sum, count) on the numeric array — e.g. <code>mean(values(monthly(rateRef)))</code> is the average of the
            monthly aggregates over the default window.
          </dd>
          <dt>Date functions</dt>
          <dd>
            daysBetween(d1, d2), extractYear(d), extractMonth(d), extractQuarter(d), extractDay(d), extractWeek(d),
            extractDayOfWeek(d), extractHour(d), formatDate(d, unit)
          </dd>
          <dt>Period functions</dt>
          <dd>
            startOfYear(d), startOfQuarter(d), startOfMonth(d), startOfWeek(d), startOfDay(d), startOfHour(d) — return
            ISO date strings anchored at the start of the period, suitable for grouping on a real time axis (unlike
            formatDate, whose outputs are sortable label strings).
          </dd>
        </dl>

        {/* <p> */}
        {/*   Within a single derive action, derives evaluate in declaration order — a later derive can reference any */}
        {/*   earlier derive by bare name. Across actions, any column produced by an earlier action (derive output, */}
        {/*   aggregate output, fold key/value names) can be referenced by name in later actions. */}
        {/* </p> */}

        <p>
          Ref columns produced by {QueryKnowledgeGraphTool.mention} carry timeseries identity. Extract datapoints using
          the fetchers (<code>datapoints</code>, <code>hourly</code>, <code>daily</code>, <code>weekly</code>,
          <code>monthly</code>) which bucket the underlying raw stream by period (default aggregate "average"). Period
          fetchers also accept a pre-fetched array, so <code>monthly(rateRef)</code> and
          <code>monthly(datapoints(rateRef))</code> are equivalent — use the explicit composition when you need a custom
          <code>start</code>/<code>end</code>.
        </p>

        <p>End-to-end example chaining derives and a rollup:</p>
        <Json
          value={{
            inputDataset: 'priority_rates',
            outputDataset: 'priority_rates_summary',
            actions: [
              {
                type: 'derive',
                derives: [
                  { name: 'latestFoo', expression: 'value(last(datapoints(fooRef)))' },
                  { name: 'monthlyAverageBar', expression: 'mean(values(monthly(barRef)))' },
                  { name: 'calculated', expression: 'latestFoo + monthlyAverageBar' },
                ],
              },
              {
                type: 'rollup',
                columns: ['region'],
                aggregations: [
                  { name: 'count', op: 'count' },
                  { name: 'avg_calculated', op: 'avg', column: 'calculated' },
                ],
              },
              { type: 'sort', keys: [{ column: 'region', direction: 'asc' }] },
            ],
          }}
        />

        <h4>Column references</h4>
        <p>
          Column names with dots, colons, dashes, spaces, or other non-identifier characters work directly in any
          column-reference field — no escaping needed (e.g. "string.externalId", "metric:volume", "kebab-name").
        </p>
        <p>
          In derive expressions, math.js tokenizes those characters, so awkwardly-named columns must be accessed via
          JavaScript-style bracket syntax on the row wrapper: <code>row["string.externalId"]</code>,
          <code>row["metric:volume"]</code>. Identifier-safe columns are still referenced by bare name (
          <code>price * quantity</code>), and both styles mix freely (<code>price + row["tax.rate"]</code>). Bracket
          access also works for nested object columns — <code>prop()</code> and <code>row.parent.child</code> resolve
          the same underlying value.
        </p>

        {/* <h4>Inline expressions in column-reference fields</h4> */}
        {/* <p> */}
        {/*   Every column-reference field — every <code>column</code> / <code>columns</code> / <code>keyColumn</code> /{' '} */}
        {/*   <code>valueColumn</code> across actions, plus filter leaf <code>column</code> — accepts either a literal */}
        {/*   column name <em>or</em> an inline math.js expression following the same syntax as a derive expression. */}
        {/*   Disambiguation is match-first: refs that match a known column (input column or an earlier action's output) are */}
        {/*   kept literal; everything else is parsed as an expression and evaluated per row before the action runs. Bare */}
        {/*   names that don't match a known column (e.g. an aggregation's output column referenced by a later sort) are */}
        {/*   also kept literal so the action can resolve them post-pipeline. */}
        {/* </p> */}
        {/* <p> */}
        {/*   This collapses the two-step pattern of "derive a metric, then filter/sort/aggregate on it" into a single */}
        {/*   inline expression. Example: */}
        {/* </p> */}
        {/* <Json */}
        {/*   value={{ */}
        {/*     actions: [ */}
        {/*       { */}
        {/*         type: 'filter', */}
        {/*         where: { */}
        {/*           type: 'and', */}
        {/*           filters: [{ column: 'production - cost', op: 'gt', numberValue: 100 }], */}
        {/*         }, */}
        {/*       }, */}
        {/*       { type: 'sort', keys: [{ column: 'mean(values(monthly(rateRef)))', direction: 'desc' }] }, */}
        {/*       { */}
        {/*         type: 'rollup', */}
        {/*         columns: ['startOfMonth(timestamp)'], */}
        {/*         aggregations: [{ name: 'total_margin', op: 'sum', column: 'production - cost' }], */}
        {/*       }, */}
        {/*     ], */}
        {/*   }} */}
        {/* /> */}
        {/**/}
        <h4>Charting timeseries data</h4>
        <p>
          Timeseries fetchers (<code>datapoints</code>, <code>hourly</code>, <code>daily</code>, <code>weekly</code>,
          <code>monthly</code>) return arrays of <code>{'{timestamp, value | average | …}'}</code> objects. To chart
          them, end your pipeline with an <code>expandTimeseries</code> action — it produces a wide-format dataset with
          a canonical <code>timestamp</code> column plus one numeric column per series, which
          {ShowDashboardTool.mention} renders directly. <strong>Do not</strong> use <code>unroll</code> /
          <code>spread</code> on datapoint-array columns (the engine refuses with <code>UNROLL_DATAPOINT_ARRAY</code> /
          <code>SPREAD_DATAPOINT_ARRAY</code>); for ranking / KPIs, use math.js helpers in <code>derive</code> instead
          (e.g. <code>mean(values(daily(ref)))</code>).
        </p>
        <p>
          <code>expandTimeseries</code>'s shape depends on three knobs: <code>columns</code> (which datapoint-array
          columns to expand), <code>splitBy</code> (which row-level column identifies each row's series — required when
          the input has more than one row), and <code>align</code> (timestamp alignment, default <code>"union"</code> —
          missing values become null). Only one <code>expandTimeseries</code> action is allowed per pipeline; place it
          at the end.
        </p>
        {/* <p> */}
        {/*   <strong>1×M — one entity, multiple metrics.</strong> No <code>splitBy</code>; output columns reuse the source */}
        {/*   column names. */}
        {/* </p> */}
        {/* <Json */}
        {/*   value={{ */}
        {/*     inputDataset: 'top_producer_string', */}
        {/*     outputDataset: 'top_producer_chart', */}
        {/*     actions: [ */}
        {/*       { */}
        {/*         type: 'derive', */}
        {/*         derives: [ */}
        {/*           { name: 'oilDaily', expression: 'daily(priorityOilRateStd)' }, */}
        {/*           { name: 'gasDaily', expression: 'daily(priorityGasRateStd)' }, */}
        {/*           { name: 'waterDaily', expression: 'daily(priorityWaterRateStd)' }, */}
        {/*         ], */}
        {/*       }, */}
        {/*       { type: 'expandTimeseries', columns: ['oilDaily', 'gasDaily', 'waterDaily'], splitBy: 'wellString.externalId' }, */}
        {/*     ], */}
        {/*   }} */}
        {/* /> */}
        {/* <p> */}
        {/*   <strong>N×1 — multiple entities, one metric.</strong> <code>splitBy</code> values become the output column */}
        {/*   names. Canonical shape for "top N producers over time" questions. */}
        {/* </p> */}
        {/* <Json */}
        {/*   value={{ */}
        {/*     inputDataset: 'foo_field_wells', */}
        {/*     outputDataset: 'top5_jan_oil', */}
        {/*     actions: [ */}
        {/*       { type: 'filter', where: { type: 'and', filters: [{ column: 'field', op: 'eq', stringValue: 'Foo' }] } }, */}
        {/*       { */}
        {/*         type: 'derive', */}
        {/*         derives: [{ name: '_rank', expression: 'mean(values(monthly(prodRef)))' }], */}
        {/*       }, */}
        {/*       { type: 'sort', keys: [{ column: '_rank', direction: 'desc' }] }, */}
        {/*       { type: 'slice', limit: 5 }, */}
        {/*       { */}
        {/*         type: 'derive', */}
        {/*         derives: [{ name: 'daily', expression: "daily(prodRef, {start: '2026-01-01', end: '2026-02-01'})" }], */}
        {/*       }, */}
        {/*       { type: 'expandTimeseries', columns: ['daily'], splitBy: 'wellString.externalId' }, */}
        {/*     ], */}
        {/*   }} */}
        {/* /> */}
        <p>
          <strong>N×M — multiple entities × multiple metrics.</strong> Output columns are named
          <code>{'<splitBy>__<column>'}</code> (e.g. <code>SE0070-S__weeklyMin</code>, <code>SE0070-S__weeklyMax</code>
          ). Each chart series binds to one of those compound columns.
        </p>
        <Json
          value={{
            inputDataset: 'top5_wells',
            outputDataset: 'top5_weekly_minmaxavg',
            actions: [
              {
                type: 'derive',
                derives: [
                  { name: 'weeklyMin', expression: "weekly(prodRef, {aggregate: 'min'})" },
                  { name: 'weeklyMax', expression: "weekly(prodRef, {aggregate: 'max'})" },
                  { name: 'weeklyAvg', expression: "weekly(prodRef, {aggregate: 'average'})" },
                ],
              },
              {
                type: 'expandTimeseries',
                columns: ['weeklyMin', 'weeklyMax', 'weeklyAvg'],
                splitBy: 'wellString.externalId',
              },
            ],
          }}
        />
      </ToolGuidance>
    ),
    config: {
      parameters: TransformDatasetInputSchema,
      render: RenderTransformDatasetTool,
      progress: ProgressTransformDatasetTool,
      description: <p>Run a pipeline of actions on a stored dataset.</p>,
      produceDataset: async ({ inputDataset, outputDataset: _outputDataset, ...specParams }, ctx): Promise<Dataset> => {
        // ctx.resolve.byName throws DatasetNotFoundError (a ToolExecutionError
        // subclass from #plugin-chat/lib/tool/errors with standard "available
        // datasets" suggestions) when the inputDataset doesn't match any
        // producing tool call — the framework translates it to the structured
        // LLM envelope automatically.
        const input = await ctx.resolve.byName(inputDataset);

        // Empty source short-circuit: return the input unchanged. Avoids spinning
        // up Arquero on a zero-row table and emits a "source is empty" message
        // without an error.
        if (input.rows.length === 0) {
          return input;
        }

        return transformStep.execute(specParams, input, ctx);
      },
      execute: ({ args, dataset }) => {
        // Empty-source short-circuit: produceDataset returned the input dataset
        // unchanged (same row count, 0 rows). Emit the minimal envelope.
        if (dataset.rows.length === 0) {
          return {
            success: true as const,
            outputDataset: args.outputDataset,
            message: `Source "${args.inputDataset}" is empty (0 rows).`,
          };
        }

        // Engine outputs ride along on dataset.meta — see transformStep.
        // `aggregations` / `groupedAggregations` are what the LLM asked for
        // when the pipeline contained an aggregate action; threading them
        // through ensures the LLM sees the engine's computed value rather than
        // having to infer it from the row-level data sample.
        const { warnings: columnWarnings, aggregations, groupedAggregations } = dataset.meta ?? {};

        const resultRows = dataset.rows as Record<string, unknown>[];
        const resultRowCount = resultRows.length;
        const autoStats = computeAutoStats(resultRows);

        const filterDescription = args.actions.some((a) => a.type === 'filter') ? 'filter applied' : undefined;

        const message = formatTransformDatasetMessage({
          inputDataset: args.inputDataset,
          outputDataset: args.outputDataset,
          totalMatched: resultRowCount,
          autoStats,
          columnWarnings,
          aggregations,
          groupedAggregations,
          followUpDirective: undefined,
          fullData: resultRows,
          originalCount: resultRowCount,
          filterDescription,
        });

        return {
          success: true as const,
          outputDataset: args.outputDataset,
          message,
        };
      },
    } satisfies ProducerToolConfig<TransformDatasetInput, TransformDatasetOutput>,
  }),
);
